import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import DraftsIcon from '@mui/icons-material/Drafts'
import SendIcon from '@mui/icons-material/Send'
import EditIcon from '@mui/icons-material/Edit'

import { FlexRow, FlexColumn, FlexEndContainer, OutlineContainer } from '../styles/Containers'
import { MenuButton, SecondaryButton } from '../common/Button'
import CreateDraftFromTemplateListButton from './CreateDraftFromTemplateListButton'
import DeleteLetterButton from './DeleteLetterButton'
import LetterTable from './LetterTable'
import Letter from './Letter'
import EditableLetter from './EditableLetter'
import DownloadLetterButton from './DownloadLetterButton'

const ContentColumn = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
`

function LettersTab({
  foiId,
  tab = 'DRAFT',
  isEditing = false,
  activeLetter,
  letters = [],
  templates = [],
  users = [],
  handleUpdateIsEditing = () => {},
  handleUpdateActiveTab = () => {},
  handleCreateDraftFromTemplate = () => {},
  handleDeleteDraft = () => {},
  handleEditDraft = () => {},
  handleUpdateLetter = () => {},
  handleViewLetter = () => {}
}) {
  const [activeTab, setActiveTab] = useState()
  const [currentIsEditing, setCurrentIsEditing] = useState(isEditing)
  const [currentActiveLetter, setCurrentActiveLetter] = useState(activeLetter)

  function stopEditing() {
    setCurrentIsEditing(false)
    handleUpdateIsEditing(false)
  }

  function handleClickTab(newTab) {
    setActiveTab(newTab)
    handleUpdateActiveTab(newTab)
    setCurrentIsEditing(false)
    handleUpdateIsEditing(false)
    setCurrentActiveLetter()
  }

  useEffect(() => {
    setActiveTab(tab)
  }, [tab])

  useEffect(() => {
    setCurrentIsEditing(isEditing)
  }, [isEditing])

  useEffect(() => {
    setCurrentActiveLetter(activeLetter)
  }, [activeLetter])

  return (
    <FlexRow>
      <FlexColumn gap="5px">
        <MenuButton active={(activeTab === 'DRAFT').toString()} onClick={() => handleClickTab('DRAFT')}>
          <DraftsIcon />
          Drafts
        </MenuButton>
        <MenuButton active={(activeTab === 'SENT').toString()} onClick={() => handleClickTab('SENT')}>
          <SendIcon />
          Sent
        </MenuButton>
      </FlexColumn>
      {activeTab && (
        <ContentColumn gap="10px">
          {!currentIsEditing && (
            <>
              {!currentActiveLetter && activeTab === 'DRAFT' && templates.length > 0 && (
                <FlexEndContainer>
                  <CreateDraftFromTemplateListButton
                    foiId={foiId}
                    letterTemplates={templates}
                    handleCreateDraftFromTemplate={handleCreateDraftFromTemplate}
                  />
                </FlexEndContainer>
              )}
              {currentActiveLetter && activeTab === 'DRAFT' && (
                <FlexEndContainer gap="5px">
                  <DeleteLetterButton state={activeTab} id={currentActiveLetter.id} name={currentActiveLetter.name} onSuccess={handleDeleteDraft} />
                  <SecondaryButton onClick={() => handleEditDraft(currentActiveLetter.id)}>
                    <EditIcon /> Edit
                  </SecondaryButton>
                </FlexEndContainer>
              )}
              {currentActiveLetter && activeTab === 'SENT' && <DownloadLetterButton id={currentActiveLetter.id} name={currentActiveLetter.name} />}
              {!currentActiveLetter ? (
                <OutlineContainer padding="15px 10px">
                  {activeTab === 'DRAFT' && (
                    <LetterTable
                      state="DRAFT"
                      letters={letters.filter(l => l.state === 'DRAFT')}
                      onEditLetter={handleEditDraft}
                      handleSelectLetter={handleViewLetter}
                    />
                  )}
                  {activeTab === 'SENT' && (
                    <LetterTable state="SENT" letters={letters.filter(l => l.state === 'SENT')} handleSelectLetter={handleViewLetter} />
                  )}
                </OutlineContainer>
              ) : (
                <OutlineContainer padding="25px 30px">
                  <Letter state={activeTab} letter={currentActiveLetter} users={users} handleViewLetter={handleViewLetter} />
                </OutlineContainer>
              )}
            </>
          )}
          {currentIsEditing && currentActiveLetter && activeTab === 'DRAFT' && (
            <EditableLetter letter={currentActiveLetter} handleUpdateLetter={handleUpdateLetter} handleCancelUpdateLetter={stopEditing} />
          )}
        </ContentColumn>
      )}
    </FlexRow>
  )
}

LettersTab.propTypes = {
  foiId: PropTypes.string.isRequired,
  tab: PropTypes.oneOf(['DRAFT', 'SENT']),
  isEditing: PropTypes.bool,
  activeLetter: PropTypes.object,
  letters: PropTypes.array,
  templates: PropTypes.array,
  users: PropTypes.array,
  handleUpdateIsEditing: PropTypes.func,
  handleUpdateActiveTab: PropTypes.func,
  handleCreateDraftFromTemplate: PropTypes.func,
  handleDeleteDraft: PropTypes.func,
  handleEditDraft: PropTypes.func,
  handleUpdateLetter: PropTypes.func,
  handleViewLetter: PropTypes.func
}

export default LettersTab
